import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IPurchaseOrderLine, IPurchaseOrderLineInput, purchaseOrderLinesAtom, purchaseOrderLinesLoadingAtom } from "./types";

type UsePurchaseOrderLines = [IPurchaseOrderLine[], {
    list: (query?: Record<string, any>) => Promise<IPurchaseOrderLine[] | null>;
    getById: (id: number) => Promise<IPurchaseOrderLine | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IPurchaseOrderLine[] | null>;
    create: (input: IPurchaseOrderLineInput) => Promise<IPurchaseOrderLine | null>;
    update: (id: number, input: IPurchaseOrderLineInput) => Promise<IPurchaseOrderLine | null>;
    remove: (id: number) => Promise<IPurchaseOrderLine | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const usePurchaseOrderLines = (): UsePurchaseOrderLines => {
    const [items, setItems] = useAtom(purchaseOrderLinesAtom);
    const setLoading = useSetAtom(purchaseOrderLinesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getPurchaseOrderLines(query: $query){ id purchase_order_id trackable_item_id order_stock_qty order_unit_qty unit_cost currency } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getPurchaseOrderLines);
            return data.getPurchaseOrderLines as IPurchaseOrderLine[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch purchase order lines.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getPurchaseOrderLineById(id:$id){ id purchase_order_id trackable_item_id order_stock_qty order_unit_qty unit_cost currency } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getPurchaseOrderLineById as IPurchaseOrderLine;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedPurchaseOrderLines(first:$first,offset:$offset,query:$query){ id purchase_order_id trackable_item_id order_stock_qty order_unit_qty unit_cost currency } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedPurchaseOrderLines as IPurchaseOrderLine[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IPurchaseOrderLineInput) => {
        try { const q = `mutation($input:PurchaseOrderLineInput!){ createPurchaseOrderLine(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createPurchaseOrderLine as IPurchaseOrderLine;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IPurchaseOrderLineInput) => {
        try { const q = `mutation($id:ID!,$input:PurchaseOrderLineInput!){ updatePurchaseOrderLine(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updatePurchaseOrderLine as IPurchaseOrderLine;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deletePurchaseOrderLine(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deletePurchaseOrderLine as IPurchaseOrderLine;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


